import { createContext, useContext, type ComponentType, type ReactNode } from 'react';
import type { ResourceSize } from '@runloop/reflex-contract';
import type { Agent, AgentEnvVar, AgentType, AxonEvent, Snapshot } from '@reflex/shared';
import type { ResourcePreviewProps } from '@reflex/plugin-api';

/**
 * Component registry for host-bound UI components.
 *
 * Some components in `@reflex/ui` depend on host-specific infrastructure
 * (deep hooks, contexts, or services that only exist in the web host).
 * Instead of stubbing them out silently, we use a registry pattern:
 *
 * 1. The web host provides real implementations via `PluginComponentProvider`
 * 2. Components in `ui/` consume from the registry
 * 3. Missing implementations throw a clear error (not silent null)
 *
 * This prevents silent UI degradation in production plugin pages.
 */

export interface AgentStreamViewProps {
  agent: Agent;
  onArchived?: () => void;
  onToggleSidebar?: () => void;
  highlightEventId?: string;
  onHighlightConsumed?: () => void;
}

export interface AgentConfigFormSectionProps {
  agentType: AgentType;
  selectedProviderId: string | null;
  model: string;
  modelEndpointId: string | null;
  reasoningEffort: string | null;
  blueprintName?: string | null;
  blueprintSelection?: { mode: 'id' | 'name'; value: string | null };
  resourceSize?: ResourceSize | null;
  /**
   * Whether to start a Docker daemon (Docker-in-Docker) inside the devbox at
   * launch — surfaced as the "dockerd" capability chip. Only rendered when
   * `onDockerdChange` is supplied.
   */
  dockerd?: boolean;
  /**
   * Minutes the devbox may sit idle before auto-suspending. `null`/omitted
   * defers to the provider default. Only rendered when
   * `onIdleTimeMinutesChange` is supplied.
   */
  idleTimeMinutes?: number | null;
  attachmentValues: Record<string, unknown>;
  envVars: AgentEnvVar[];
  hasStoredSecrets?: boolean;
  onAgentTypeChange: (next: AgentType) => void;
  onModelChange: (modelId: string) => void;
  onReasoningEffortChange: (effort: string) => void;
  onEndpointChange: (endpointId: string) => void;
  onBlueprintChange: (next: string | null) => void;
  /**
   * Org devbox snapshots offered in the blueprint picker (own "Snapshots"
   * group, mutually exclusive with blueprints). Callers fetch and
   * capability-gate these; omit/empty hides the group. Requires
   * `onSnapshotChange` to take effect.
   */
  snapshots?: Snapshot[];
  /** Currently-selected snapshot id (`snp_*`), or `null`. */
  snapshotId?: string | null;
  /** Snapshot selection callback. Picking a snapshot clears the blueprint. */
  onSnapshotChange?: (next: string | null) => void;
  onResourceSizeChange?: (next: ResourceSize | null) => void;
  /** Toggle the "dockerd" capability. When omitted, the chip is hidden. */
  onDockerdChange?: (next: boolean) => void;
  /**
   * Set the idle-time minutes before auto-suspend. When omitted, the
   * Auto-suspend chip is hidden.
   */
  onIdleTimeMinutesChange?: (next: number | null) => void;
  onAttachmentChange: (id: string, config: unknown, pluginName: string) => void;
  onEnvVarsChange: (next: AgentEnvVar[]) => void;
  /**
   * Labels (`alb_*`) put on every run this configuration launches, and the
   * group (`agp_*`) each is filed into. Each chip renders only when its
   * handler is supplied; the catalogs come from the host's org-scoped hooks.
   */
  labelIds?: string[];
  onLabelIdsChange?: (next: string[]) => void;
  groupId?: string | null;
  onGroupIdChange?: (next: string | null) => void;
  secretsOwner?: unknown;
  /**
   * Currently-selected model-provider secret (`mps_*`) pinned to the
   * config, or `null`. When `onProviderSecretChange` is supplied the
   * section renders a `ModelProviderSecretPicker` for the resolved
   * provider; omit both to hide the picker (e.g. the launch dialog renders
   * its own).
   */
  providerSecretId?: string | null;
  onProviderSecretChange?: (id: string | null) => void;
  /**
   * Owner of the resource the key is bound to, used to scope which keys
   * the picker offers. The kind is derived from the id's prefix (user
   * -> user+team+org, team -> team+org, organization -> org-only).
   */
  providerKeyOwner?: {
    ownerId: string;
  };
  /**
   * When true, the section omits its own agent settings chip. Launch composers
   * render the chip in the chat composer toolbar instead.
   */
  hideSettingsChip?: boolean;
  /**
   * When true, the section omits the "Attachments" sub-section. Launch composers
   * add attachments through the composer's "+"/`@` menu (surfaced as chips in
   * the applied-context row) instead of a per-kind grid.
   */
  hideAttachments?: boolean;
  /**
   * Skip the collapsible Attachments/Advanced headers and render the
   * editors directly (advanced chips, then attachments). The flow inspector
   * supplies its own section chrome around this block.
   */
  bare?: boolean;
  /**
   * After a submit attempt that was blocked because the provider needs a key
   * and none is selected, flip this on so the settings chip surfaces an
   * explicit "choose a provider key" status next to the highlighted picker.
   * Pre-submit callers leave it false.
   */
  showMissingKeyError?: boolean;
  /**
   * Status copy shown when {@link showMissingKeyError} is true. Launch
   * composers keep the default; the persona editor overrides it so the
   * message matches "save" rather than "launch".
   */
  missingKeyErrorText?: string;
}

export interface AgentSetupProgressProps {
  setupEvents: AxonEvent[];
  isComplete: boolean;
  agentId?: string;
}

export interface ResourcePreviewLoaderProps extends ResourcePreviewProps {
  kind: string;
  fallbackName?: string;
}

/**
 * Props for the host-bound entity-id badge — the same shape a `text` content
 * scanner passes its component (`match` is the full id, `groups` are the regex
 * capture groups, group 1 being the 3-letter prefix). The host implementation
 * resolves the prefix to an icon + hover preview via the resource-preview
 * registry; the `@reflex/ui` fallback renders a plain copyable chip.
 */
export interface EntityIdBadgeHostProps {
  match: string;
  groups: string[];
}

export interface PluginComponentRegistryValue {
  AgentStreamView?: ComponentType<AgentStreamViewProps>;
  AgentConfigFormSection?: ComponentType<AgentConfigFormSectionProps>;
  AgentSetupProgress?: ComponentType<AgentSetupProgressProps>;
  ResourcePreview?: ComponentType<ResourcePreviewLoaderProps>;
  EntityIdBadge?: ComponentType<EntityIdBadgeHostProps>;
  /**
   * Resolve an entity-id prefix (`prs`, `npl`, `agt`, …) to the registered
   * kind label ("Persona", "Network Policy") from the same resource-preview
   * registry the {@link EntityIdBadge} host uses — so a surface that labels
   * an id (Chief's approval card) can say what the id *is*, not just show
   * it. `undefined` when no preview owns the prefix; callers fall back to
   * their own wording. Not a component, but it rides in this registry
   * because it is the same host-owned lookup the badge is built on.
   */
  entityIdKindLabel?: (prefix: string) => string | undefined;
}

const PluginComponentRegistryContext = createContext<PluginComponentRegistryValue | null>(null);

interface PluginComponentProviderProps {
  components: PluginComponentRegistryValue;
  children: ReactNode;
}

/**
 * Provider for registering host-bound component implementations.
 *
 * Usage in web host:
 * ```tsx
 * import { PluginComponentProvider } from '@reflex/ui/components/registry/PluginComponentRegistry';
 * import { AgentStreamView } from '@/components/agent/AgentStreamView';
 *
 * <PluginComponentProvider components={{ AgentStreamView }}>
 *   {children}
 * </PluginComponentProvider>
 * ```
 */
export function PluginComponentProvider({ components, children }: PluginComponentProviderProps) {
  return (
    <PluginComponentRegistryContext.Provider value={components}>
      {children}
    </PluginComponentRegistryContext.Provider>
  );
}

/**
 * Hook to access a registered component.
 * Throws if the component is not registered (prevents silent failures).
 */
export function useRegisteredComponent<K extends keyof PluginComponentRegistryValue>(
  name: K,
): NonNullable<PluginComponentRegistryValue[K]> {
  const registry = useContext(PluginComponentRegistryContext);
  const component = registry?.[name];

  if (!component) {
    throw new Error(
      `[PluginComponentRegistry] Component "${name}" is not registered. ` +
        `Wrap your app with <PluginComponentProvider components={{ ${name}: ... }}> ` +
        `to provide the host implementation. This component requires host-specific ` +
        `infrastructure and cannot be rendered in isolation.`,
    );
  }

  return component as NonNullable<PluginComponentRegistryValue[K]>;
}

/**
 * Hook to optionally access a registered component (returns undefined if not registered).
 * Use this for components that have a fallback behavior.
 */
export function useOptionalRegisteredComponent<K extends keyof PluginComponentRegistryValue>(
  name: K,
): PluginComponentRegistryValue[K] | undefined {
  const registry = useContext(PluginComponentRegistryContext);
  return registry?.[name];
}

/**
 * Check if the component registry has been initialized.
 */
export function useHasComponentRegistry(): boolean {
  const registry = useContext(PluginComponentRegistryContext);
  return registry !== null;
}
